<template>
  <header
    class="sticky top-0 z-50 border-b border-[var(--header-border)] bg-[var(--header-bg)] text-[var(--header-text)] backdrop-blur-2xl transition-colors duration-300"
  >
    <nav
      class="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-6 lg:px-8"
    >
      <!-- LOGO -->
      <router-link
        to="/"
        class="group flex items-center gap-3"
        @click="close"
      >
        <div
          class="grid h-9 w-9 place-items-center rounded-2xl bg-[var(--header-logo-bg)] text-xs font-semibold text-[var(--header-logo-text)] shadow-sm ring-1 ring-[var(--header-border)] transition duration-300 group-hover:scale-[1.04]"
        >
          G
        </div>

        <span
          class="text-sm font-semibold tracking-tight text-[var(--header-text)] transition-colors duration-300"
        >
          Grivaro Studio
        </span>
      </router-link>

      <!-- DESKTOP NAV -->
      <ul
        class="hidden items-center gap-8 text-sm font-medium md:flex"
      >
        <li v-for="item in navigation" :key="item.path">
          <router-link
            :to="item.path"
            class="relative py-2 text-[var(--header-muted)] transition duration-300 hover:text-[var(--header-text)]"
            :class="isActive(item.path)"
          >
            {{ item.label }}

            <span
              v-if="isCurrent(item.path)"
              class="absolute inset-x-0 -bottom-[1.05rem] mx-auto h-px bg-[var(--header-text)]"
            ></span>
          </router-link>
        </li>
      </ul>

      <!-- RIGHT ACTIONS -->
      <div class="flex items-center gap-2">
        <!-- THEME TOGGLE -->
        <button
          type="button"
          class="group grid h-10 w-10 place-items-center rounded-full border border-[var(--header-border)] bg-[var(--header-control)] text-[var(--header-text)] shadow-sm transition duration-300 hover:-translate-y-0.5 hover:bg-[var(--header-control-hover)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--header-focus)]"
          :aria-label="isDark ? 'Ativar tema claro' : 'Ativar tema escuro'"
          :title="isDark ? 'Tema claro' : 'Tema escuro'"
          @click="toggleTheme"
        >
          <!-- SUN -->
          <svg
            v-if="isDark"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            class="h-[18px] w-[18px] transition duration-300 group-hover:rotate-12"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="4" />

            <path
              stroke-linecap="round"
              d="M12 2v2M12 20v2M4.93 4.93l1.42 1.42M17.65 17.65l1.42 1.42M2 12h2M20 12h2M4.93 19.07l1.42-1.42M17.65 6.35l1.42-1.42"
            />
          </svg>

          <!-- MOON -->
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            class="h-[18px] w-[18px] transition duration-300 group-hover:-rotate-12"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z"
            />
          </svg>
        </button>

        <!-- WHATSAPP -->
        <a
          :href="whatsHref"
          target="_blank"
          rel="noopener"
          class="hidden h-10 items-center justify-center rounded-full bg-[var(--header-primary)] px-5 text-sm font-semibold text-[var(--header-primary-text)] shadow-sm transition duration-300 hover:-translate-y-0.5 hover:opacity-90 sm:inline-flex"
        >
          Fale sobre seu projeto
        </a>

        <!-- MOBILE MENU -->
        <button
          type="button"
          class="grid h-10 w-10 place-items-center rounded-full border border-[var(--header-border)] bg-[var(--header-control)] text-[var(--header-text)] transition duration-300 hover:bg-[var(--header-control-hover)] md:hidden"
          @click="open = !open"
          aria-label="Abrir menu"
          :aria-expanded="open ? 'true' : 'false'"
        >
          <svg
            v-if="!open"
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="1.8"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M4 7h16M4 12h16M4 17h16"
            />
          </svg>

          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="1.8"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M6 18 18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </nav>

    <!-- MOBILE MENU -->
    <transition name="menu">
      <div
        v-show="open"
        class="border-t border-[var(--header-border)] bg-[var(--header-mobile)] backdrop-blur-2xl md:hidden"
      >
        <div
          class="mx-auto max-w-7xl px-5 py-4 sm:px-6 lg:px-8"
        >
          <ul class="flex flex-col gap-1 text-sm font-semibold">
            <li
              v-for="item in navigation"
              :key="item.path"
            >
              <router-link
                :to="item.path"
                @click="close"
                class="block rounded-2xl px-4 py-3 text-[var(--header-muted)] transition duration-300 hover:bg-[var(--header-control)] hover:text-[var(--header-text)]"
                :class="isActive(item.path)"
              >
                {{ item.label }}
              </router-link>
            </li>
          </ul>

          <div
            class="mt-4 border-t border-[var(--header-border)] pt-4"
          >
            <div
              class="mb-3 flex items-center justify-between rounded-2xl bg-[var(--header-control)] px-4 py-3"
            >
              <div>
                <p class="text-sm font-semibold text-[var(--header-text)]">
                  Aparência
                </p>

                <p class="mt-0.5 text-xs text-[var(--header-muted)]">
                  {{ isDark ? 'Tema escuro ativo' : 'Tema claro ativo' }}
                </p>
              </div>

              <button
                type="button"
                class="grid h-9 w-9 place-items-center rounded-full bg-[var(--header-primary)] text-[var(--header-primary-text)]"
                @click="toggleTheme"
                :aria-label="isDark ? 'Ativar tema claro' : 'Ativar tema escuro'"
              >
                <svg
                  v-if="isDark"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  class="h-4 w-4"
                >
                  <circle cx="12" cy="12" r="4" />

                  <path
                    stroke-linecap="round"
                    d="M12 2v2M12 20v2M4.93 4.93l1.42 1.42M17.65 17.65l1.42 1.42M2 12h2M20 12h2M4.93 19.07l1.42-1.42M17.65 6.35l1.42-1.42"
                  />
                </svg>

                <svg
                  v-else
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  class="h-4 w-4"
                >
                  <path
                    d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z"
                  />
                </svg>
              </button>
            </div>

            <a
              :href="whatsHref"
              target="_blank"
              rel="noopener"
              class="inline-flex h-12 w-full items-center justify-center rounded-full bg-[var(--header-primary)] px-7 text-sm font-semibold text-[var(--header-primary-text)] transition hover:opacity-90"
            >
              Fale sobre seu projeto
            </a>
          </div>
        </div>
      </div>
    </transition>
  </header>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

const THEME_KEY = 'Grivaro-theme'

const open = ref(false)
const isDark = ref(false)

const route = useRoute()

const whatsHref = 'https://wa.me/5577999631644'

const navigation = [
  {
    label: 'Início',
    path: '/'
  },
  {
    label: 'Soluções',
    path: '/solutions'
  },
  {
    label: 'Projetos',
    path: '/projects'
  },
  {
    label: 'Templates',
    path: '/templates'
  },
  {
    label: 'Blog',
    path: '/blog'
  }
]

function isCurrent(path) {
  if (path === '/') {
    return route.path === '/'
  }

  return route.path.startsWith(path)
}

function isActive(path) {
  return isCurrent(path)
    ? 'text-[var(--header-text)]'
    : ''
}

function applyTheme(theme) {
  const dark = theme === 'dark'

  isDark.value = dark

  document.documentElement.dataset.theme = theme
  document.documentElement.style.colorScheme = theme
}

function getInitialTheme() {
  const savedTheme = localStorage.getItem(THEME_KEY)

  if (
    savedTheme === 'light' ||
    savedTheme === 'dark'
  ) {
    return savedTheme
  }

  return window.matchMedia(
    '(prefers-color-scheme: dark)'
  ).matches
    ? 'dark'
    : 'light'
}

function toggleTheme() {
  const nextTheme = isDark.value
    ? 'light'
    : 'dark'

  localStorage.setItem(
    THEME_KEY,
    nextTheme
  )

  applyTheme(nextTheme)
}

function close() {
  open.value = false
}

onMounted(() => {
  applyTheme(getInitialTheme())
})
</script>

<style scoped>
.menu-enter-active,
.menu-leave-active {
  transition:
    opacity 0.22s ease,
    transform 0.22s ease;
}

.menu-enter-from,
.menu-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>

<style>
:root {
  --header-bg: rgba(255, 255, 255, 0.76);

  --header-mobile:
    rgba(255, 255, 255, 0.94);

  --header-text:
    #18181b;

  --header-muted:
    #71717a;

  --header-border:
    rgba(24, 24, 27, 0.08);

  --header-control:
    rgba(245, 245, 247, 0.82);

  --header-control-hover:
    rgba(228, 228, 231, 0.85);

  --header-primary:
    #09090b;

  --header-primary-text:
    #ffffff;

  --header-logo-bg:
    #09090b;

  --header-logo-text:
    #ffffff;

  --header-focus:
    rgba(24, 24, 27, 0.1);
}

html[data-theme='dark'] {
  --header-bg:
    rgba(9, 10, 12, 0.76);

  --header-mobile:
    rgba(9, 10, 12, 0.94);

  --header-text:
    #fafafa;

  --header-muted:
    #a1a1aa;

  --header-border:
    rgba(255, 255, 255, 0.09);

  --header-control:
    rgba(255, 255, 255, 0.06);

  --header-control-hover:
    rgba(255, 255, 255, 0.10);

  --header-primary:
    #ffffff;

  --header-primary-text:
    #09090b;

  --header-logo-bg:
    #ffffff;

  --header-logo-text:
    #09090b;

  --header-focus:
    rgba(255, 255, 255, 0.12);
}
</style>